import axios from '@/plugins/axios'

// Members read the v2 route; `username` switches to its public twin, which the
// public collection page uses.
const boardPath = (collectionId, username) =>
  username
    ? `/v1/public/u/${username}/collections/${collectionId}/tierlist`
    : `/v2/collections/${collectionId}/tierlist`

/**
 * A Board: the resolved layout settings, the version's placements per Tier in
 * collection order, and the cards those placements show, side-loaded once.
 * `version` is a holder's userId or a TIER_LIST_VIEW value (the Community
 * average, the comparison); without it the default rule picks the viewer's own
 * or the owner's version (ADR-0009).
 */
export const getBoard = (collectionId, { username = null, version = null } = {}) =>
  axios.get(boardPath(collectionId, username), version ? { params: { version } } : undefined)

/**
 * One Move: `{ target, resourceType, resourceId, tierId, index }`, `tierId: null`
 * back to the Queue. Answers the Version's visible projection
 * `{ userId, versionId, rev, tiers }`. A PUT the axios interceptors never retry
 * on a network error — a stale re-send would be last-writer-wins.
 */
export const movePlacement = (collectionId, body, { username = null } = {}) =>
  axios.put(`${boardPath(collectionId, username)}/placements`, body)

/**
 * One page of the Versions a viewer can switch to: `{ results: [{ userId, user,
 * updatedAt, isOwn }], next, community }`. Page 1 (no `next`) starts with the
 * owner's row on top of `limit` member holders and carries the Community's
 * `{ count }` (null without one); `next` is opaque and goes back verbatim.
 */
export const getVersions = (collectionId, { username = null, limit, next = null }) =>
  axios.get(`${boardPath(collectionId, username)}/versions`, { params: { limit, ...(next && { next }) } })

/** Deletes the viewer's own version; answers it empty `{ userId, versionId: null, rev: 0, tiers: [] }`. */
export const resetOwnVersion = (collectionId, { username = null } = {}) =>
  axios.delete(`${boardPath(collectionId, username)}/versions/own`)

/**
 * Saves Layout settings: a non-empty subset of `{ tiers, participation, items }`,
 * each sent section replacing that section whole. Answers the resolved
 * `tierList`, new Tiers with their server-minted `_id`.
 */
export const patchLayoutSettings = (collectionId, patch) =>
  axios.patch(`/v2/collections/${collectionId}/layouts/tierlist`, patch)
